/**
 * Legal document loader with English as the source of truth.
 *
 * Legal copy is deliberately kept out of the main UI translation files.
 * English documents always exist; translations are added later as
 * `<lang>.privacy.json` / `<lang>.terms.json`, registered below, and served
 * only when the language is enabled in LANGUAGE_CONFIG. Missing, disabled, or
 * stale translations fall back to English (per section, by revision number).
 */

import {
  DEFAULT_LANGUAGE,
  isLanguageEnabled,
  isSupportedLanguage,
  type SupportedLanguage,
} from '../languages';
import enPrivacy from './en.privacy.json';
import enTerms from './en.terms.json';

export type LegalDocumentType = 'privacy' | 'terms';

export type LegalSection = {
  id: string;
  revision: number;
  heading: string;
  body: string[];
};

export type LegalDocument = {
  documentId: string;
  language: string;
  version: string;
  title: string;
  effectiveDate: string;
  englishControlsNotice: string;
  sections: LegalSection[];
};

/**
 * Register translated legal documents here once they exist and have been
 * reviewed, e.g. `privacy: { en: enPrivacy, es: esPrivacy }`.
 */
const LEGAL_DOCUMENTS: Record<
  LegalDocumentType,
  Partial<Record<SupportedLanguage, LegalDocument>>
> = {
  privacy: { en: enPrivacy },
  terms: { en: enTerms },
};

function englishDocument(documentType: LegalDocumentType): LegalDocument {
  const document = LEGAL_DOCUMENTS[documentType][DEFAULT_LANGUAGE];
  if (!document) {
    throw new Error(`Missing English legal document: ${documentType}`);
  }
  return document;
}

/**
 * The document exactly as registered for a language, or null. English is
 * always available; other languages only when registered AND enabled.
 */
export function getLegalDocument(
  documentType: LegalDocumentType,
  language: string,
): LegalDocument | null {
  if (!isSupportedLanguage(language)) return null;
  if (language !== DEFAULT_LANGUAGE && !isLanguageEnabled(language)) {
    return null;
  }
  return LEGAL_DOCUMENTS[documentType][language] ?? null;
}

export function isLegalTranslationAvailable(
  documentType: LegalDocumentType,
  language: string,
): boolean {
  return getLegalDocument(documentType, language) !== null;
}

/**
 * A translation is fresh when every English section exists in it with a
 * revision at least as new as the English one.
 */
export function isLegalTranslationFresh(
  documentType: LegalDocumentType,
  language: string,
): boolean {
  const translated = getLegalDocument(documentType, language);
  if (!translated) return false;
  const english = englishDocument(documentType);
  return english.sections.every((section) => {
    const match = translated.sections.find((item) => item.id === section.id);
    return !!match && match.revision >= section.revision;
  });
}

/**
 * The document to show for a requested language. Falls back to English
 * entirely when the translation is missing or disabled, and per section when
 * a translated section is older than the English one.
 */
export function getLegalDocumentWithFallback(
  documentType: LegalDocumentType,
  requestedLanguage: string,
): LegalDocument {
  const english = englishDocument(documentType);
  const translated = getLegalDocument(documentType, requestedLanguage);
  if (!translated || translated === english) return english;

  return {
    ...translated,
    sections: english.sections.map((section) => {
      const match = translated.sections.find((item) => item.id === section.id);
      return match && match.revision >= section.revision ? match : section;
    }),
  };
}
